// 학생 확인평가 응시 (/student/diagnosis/quiz) — 본인 학년·선택 과목의 공개 회차 목록 → 문항 풀이 → 결과.
// 풀이 중 답안은 localStorage 초안으로 저장해 이탈 후 복구한다. 제출은 submitQuizAttempt.
// 서술형 문항은 '채점 대기'(earned === null)로 표시되고 선생님이 0~배점 사이 점수로 채점하면 갱신된다.

import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, ClipboardCheck, CheckCircle2, XCircle, RotateCcw, Loader, Clock } from 'lucide-react'
import { useAuth } from '../../context/AuthContext.jsx'
import { useData } from '../../context/DataContext.jsx'
import { hasPendingGrading, isPendingAnswer, answerEarned, answerPoints } from '../../utils/quizGrading.js'
import { QUIZ_SUBJECTS, DEFAULT_QUIZ_SUBJECT } from '../../utils/quizSubjects.js'
import { quizFileUrl, isImageAttachment } from '../../lib/quizFiles.js'
import { AttachmentChips } from '../../components/counseling/AttachmentField.jsx'

// 문항 첨부 렌더 — 이미지는 인라인, PDF는 열람 칩 (응시·결과 화면 공용)
function QuestionAttachments({ attachments }) {
  if (!attachments?.length) return null
  const images = attachments.filter(isImageAttachment)
  const files = attachments.filter((a) => !isImageAttachment(a))
  return (
    <div className="mt-2 space-y-2">
      {images.map((a) => (
        <img key={a.path} src={quizFileUrl(a.path)} alt={a.name} className="rounded-lg max-w-full border border-gray-100" />
      ))}
      <AttachmentChips attachments={files} fileUrl={quizFileUrl} />
    </div>
  )
}

const DRAFT_PREFIX = 'quiz_draft'

function getDraftKey(studentId, setId) {
  if (!studentId || !setId) return null
  return `${DRAFT_PREFIX}:${studentId}:${setId}`
}

function readDraft(key) {
  if (!key) return null
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null')
    if (!parsed || typeof parsed !== 'object') return null
    return {
      step: Number.isInteger(parsed.step) ? parsed.step : 0,
      rawByQid: parsed.rawByQid && typeof parsed.rawByQid === 'object' ? parsed.rawByQid : {},
    }
  } catch {
    return null
  }
}

function removeDraft(key) {
  if (!key) return
  try {
    localStorage.removeItem(key)
  } catch {
    // localStorage 접근 실패는 응시 흐름을 막지 않는다.
  }
}

export default function QuizTab() {
  const { currentUser } = useAuth()
  const { data, submitQuizAttempt } = useData()

  const studentId = currentUser?.id
  const myGrade = currentUser?.grade ?? data.students.find((s) => s.id === studentId)?.grade ?? ''

  const [activeSubject, setActiveSubject] = useState(DEFAULT_QUIZ_SUBJECT)

  // 학생 본인 학년 + 선택 과목 회차만
  const mySets = useMemo(
    () => data.quizSets
      .filter((s) => (s.grade === myGrade || s.grade === '전체') && s.isPublished && (s.subject ?? '국어') === activeSubject)
      .sort((a, b) => a.round - b.round),
    [data.quizSets, myGrade, activeSubject]
  )

  const myAttempts = useMemo(
    () => data.quizAttempts.filter((a) => a.studentId === studentId),
    [data.quizAttempts, studentId]
  )

  const [activeSetId, setActiveSetId] = useState(null)
  const [mode, setMode] = useState('list') // 'list' | 'playing' | 'result'
  const [step, setStep] = useState(0)
  const [rawByQid, setRawByQid] = useState({})
  const [resultAttempt, setResultAttempt] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const activeSet = mySets.find((s) => s.id === activeSetId)
  const activeQuestions = useMemo(
    () => data.quizQuestions
      .filter((q) => q.quizSetId === activeSetId)
      .sort((a, b) => a.orderNo - b.orderNo),
    [data.quizQuestions, activeSetId]
  )
  // 결과 화면에서 응시 기록의 questionId로 문제 본문 lookup
  const activeQuestionsById = useMemo(
    () => Object.fromEntries(activeQuestions.map((q) => [q.id, q])),
    [activeQuestions]
  )
  const draftKey = useMemo(() => getDraftKey(studentId, activeSetId), [studentId, activeSetId])
  const hasDraftAnswer = useMemo(
    () => Object.values(rawByQid).some((v) => String(v ?? '').trim().length > 0),
    [rawByQid]
  )

  useEffect(() => {
    if (mode !== 'playing' || !draftKey) return
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ step, rawByQid, updatedAt: new Date().toISOString() })
      )
    } catch {
      // 저장 공간 부족 등은 사용자 응시를 막지 않는다.
    }
  }, [draftKey, mode, rawByQid, step])

  useEffect(() => {
    if (mode !== 'playing' || !hasDraftAnswer) return
    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasDraftAnswer, mode])

  useEffect(() => {
    if (mode !== 'playing' || activeQuestions.length === 0) return
    if (step >= activeQuestions.length) setStep(activeQuestions.length - 1)
  }, [activeQuestions.length, mode, step])

  function openSet(setId) {
    const existing = myAttempts.find((a) => a.quizSetId === setId)
    const set = mySets.find((s) => s.id === setId)
    // 점수전용(외부시험) 회차는 앱 내 응시가 없다 — 점수 입력 전엔 진입 차단
    if (set?.isScoreOnly && !existing) return
    const nextDraftKey = getDraftKey(studentId, setId)
    const draft = readDraft(nextDraftKey)
    setActiveSetId(setId)
    if (existing) {
      removeDraft(nextDraftKey)
      setStep(0)
      setResultAttempt(existing)
      setMode('result')
    } else {
      setStep(draft?.step ?? 0)
      setRawByQid(draft?.rawByQid ?? {})
      setMode('playing')
    }
  }

  function backToList({ clearDraft = false } = {}) {
    if (clearDraft) removeDraft(draftKey)
    setActiveSetId(null)
    setMode('list')
    setResultAttempt(null)
    setRawByQid({})
    setStep(0)
  }

  function setRaw(qid, value) {
    setRawByQid((prev) => ({ ...prev, [qid]: value }))
  }

  async function handleSubmit() {
    if (submitting) return
    setSubmitting(true)
    try {
      const attempt = await submitQuizAttempt(studentId, activeSetId, rawByQid)
      removeDraft(draftKey)
      setResultAttempt(attempt)
      setMode('result')
    } catch {
      // 저장 실패는 전역 Toast가 표면화한다.
    } finally {
      setSubmitting(false)
    }
  }

  function confirmCancelAttempt() {
    if (hasDraftAnswer && !window.confirm('응시를 취소하면 입력한 답안이 사라집니다. 정말 나가시겠습니까?')) {
      return
    }
    backToList({ clearDraft: true })
  }

  // ── 목록 화면 ──────────────────────────────────────────────
  if (mode === 'list') {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ClipboardCheck size={20} />
            </div>
            <div>
              <p className="text-xs opacity-80">확인평가</p>
              <h2 className="text-lg font-bold">{myGrade || '학년 미설정'} 쪽지시험</h2>
            </div>
          </div>
          <p className="text-xs opacity-80 leading-relaxed">
            교재 개념을 점검합니다. 회차당 1회만 응시할 수 있습니다.
          </p>
        </div>

        {/* 과목 탭 */}
        <div className="grid grid-cols-4 gap-1.5">
          {QUIZ_SUBJECTS.map((subj) => (
            <button
              key={subj}
              onClick={() => setActiveSubject(subj)}
              className={`py-2 rounded-xl text-sm font-bold transition ${
                activeSubject === subj
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-white text-gray-500 border border-gray-200'
              }`}
            >
              {subj}
            </button>
          ))}
        </div>

        {mySets.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 text-center text-sm text-gray-400">
            아직 배포된 {activeSubject} 회차가 없습니다.
          </div>
        ) : (
          <div className="space-y-3">
            {mySets.map((s) => {
              const attempt = myAttempts.find((a) => a.quizSetId === s.id)
              const done = !!attempt
              const waitingScore = s.isScoreOnly && !done
              return (
                <button
                  key={s.id}
                  onClick={() => openSet(s.id)}
                  disabled={waitingScore}
                  className="w-full text-left bg-white rounded-2xl p-4 border border-gray-100 shadow-sm active:scale-[0.98] transition-all disabled:active:scale-100"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-bold text-gray-800 text-sm">{s.title}</p>
                    {done ? (
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                        {s.isScoreOnly ? '점수 등록' : '응시 완료'}
                      </span>
                    ) : waitingScore ? (
                      <span className="text-[10px] bg-gray-100 text-gray-500 font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                        점수 입력 대기
                      </span>
                    ) : (
                      <span className="text-[10px] bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                        미응시
                      </span>
                    )}
                  </div>
                  {s.description && <p className="text-xs text-gray-500 leading-relaxed">{s.description}</p>}
                  {waitingScore && (
                    <p className="text-xs text-gray-400 mt-2">외부 시험 점수를 선생님이 입력하면 여기에서 확인할 수 있어요.</p>
                  )}
                  {done && (
                    <p className="text-xs text-emerald-600 font-semibold mt-2">
                      {attempt.score} / {attempt.total} 점
                      {hasPendingGrading(attempt) && (
                        <span className="text-amber-600"> · 채점 대기 중</span>
                      )}
                      {!s.isScoreOnly && ' · 결과 보기'}
                    </p>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // ── 응시 화면 ──────────────────────────────────────────────
  if (mode === 'playing' && activeSet) {
    if (activeQuestions.length === 0) {
      return (
        <div className="py-6 text-center text-sm text-gray-400">
          문제를 불러오는 중...
          <button onClick={confirmCancelAttempt} className="block mx-auto mt-4 text-indigo-600 text-xs">목록으로</button>
        </div>
      )
    }

    const q = activeQuestions[step]
    const progress = ((step + 1) / activeQuestions.length) * 100
    const currentValue = rawByQid[q.id] ?? ''
    const isLast = step === activeQuestions.length - 1
    const canNext = currentValue.trim().length > 0
    const unanswered = activeQuestions.filter((qq) => !(rawByQid[qq.id] ?? '').trim()).length

    return (
      <div className="py-4 space-y-4 px-1">
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-400">
            <span>{step + 1} / {activeQuestions.length}</span>
            <span className="truncate ml-2">{activeSet.title}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100 min-h-[120px]">
          <p className="text-[11px] font-bold text-emerald-600 mb-2">문제 {step + 1}</p>
          <p className="text-sm font-medium text-gray-800 leading-relaxed whitespace-pre-wrap">{q.question}</p>
          <QuestionAttachments attachments={q.attachments} />
          {q.hint && <p className="text-xs text-gray-400 mt-2">힌트: {q.hint}</p>}
        </div>

        <div>
          {q.type === 'essay' ? (
            <textarea
              value={currentValue}
              onChange={(e) => setRaw(q.id, e.target.value)}
              placeholder="답안을 작성하세요"
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-emerald-400 bg-white resize-none"
              autoFocus
            />
          ) : (
            <input
              type="text"
              value={currentValue}
              onChange={(e) => setRaw(q.id, e.target.value)}
              placeholder="정답을 입력하세요"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-emerald-400 bg-white"
              autoFocus
            />
          )}
          <p className="text-[11px] text-gray-400 mt-1.5 px-1">
            {q.type === 'essay'
              ? '서술형 문항은 제출 후 선생님이 직접 채점합니다.'
              : '공백과 대소문자는 채점 시 무시됩니다.'}
          </p>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 disabled:opacity-30"
          >
            <ChevronLeft size={16} /> 이전
          </button>
          {!isLast ? (
            <button
              onClick={() => setStep((s) => Math.min(activeQuestions.length - 1, s + 1))}
              disabled={!canNext}
              className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              다음 <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !canNext}
              className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              {submitting ? (<><Loader size={14} className="animate-spin" /> 제출 중...</>) : '제출하기'}
            </button>
          )}
        </div>

        {isLast && unanswered > 0 && (
          <p className="text-xs text-amber-600 text-center">
            미입력 문항 {unanswered}개가 있습니다. 빈 답안은 오답 처리됩니다.
          </p>
        )}

        <div className="pt-6">
          <button
            onClick={confirmCancelAttempt}
            className="block w-full text-xs text-gray-400 py-3 underline underline-offset-2"
          >
          나가기 (응시 취소)
          </button>
        </div>
      </div>
    )
  }

  // ── 결과 화면 ──────────────────────────────────────────────
  if (mode === 'result' && activeSet && resultAttempt) {
    const pct = resultAttempt.total > 0 ? Math.round((resultAttempt.score / resultAttempt.total) * 100) : 0

    return (
      <div className="py-4 space-y-4 px-1">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <p className="text-xs opacity-80 mb-1">{activeSet.title}</p>
          <div className="flex items-end gap-2">
            <h2 className="text-3xl font-bold">{resultAttempt.score}</h2>
            <p className="text-sm opacity-80 pb-1">/ {resultAttempt.total} 점</p>
            <span className="ml-auto text-sm font-bold bg-white/20 rounded-full px-2.5 py-0.5">{pct}%</span>
          </div>
          {hasPendingGrading(resultAttempt) && (
            <p className="text-xs mt-2 bg-white/20 rounded-lg px-2.5 py-1.5 leading-relaxed">
              서술형 채점 대기 문항이 있습니다. 선생님 채점 후 점수가 갱신됩니다.
            </p>
          )}
          {activeSet.isScoreOnly && (
            <p className="text-xs mt-2 bg-white/20 rounded-lg px-2.5 py-1.5 leading-relaxed">
              외부 시험 점수입니다. 문항별 결과는 제공되지 않습니다.
            </p>
          )}
        </div>

        <div className="space-y-2">
          {resultAttempt.answers.map((a, idx) => {
            const q = activeQuestionsById[a.questionId]
            if (!q) return null
            const pending = isPendingAnswer(a)
            const earned = answerEarned(a)
            const pts = answerPoints(a)
            const full = !pending && earned >= pts
            const zero = !pending && earned === 0
            // 색 규칙: 대기·부분점수 amber / 만점 초록 / 0점 빨강
            const tone = pending || (!full && !zero) ? 'amber' : full ? 'emerald' : 'red'
            return (
              <div
                key={a.questionId}
                className={`bg-white rounded-2xl p-4 border ${
                  tone === 'amber' ? 'border-amber-100' : tone === 'emerald' ? 'border-emerald-100' : 'border-red-100'
                }`}
              >
                <div className="flex items-start gap-2 mb-2">
                  {pending
                    ? <Clock size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    : full
                      ? <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                      : zero
                        ? <XCircle      size={18} className="text-red-500     flex-shrink-0 mt-0.5" />
                        : <CheckCircle2 size={18} className="text-amber-500   flex-shrink-0 mt-0.5" />
                  }
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                      <span className="text-[11px] font-bold text-gray-400 mr-1">Q{idx + 1}.</span>
                      {q.question}
                    </p>
                    <QuestionAttachments attachments={q.attachments} />
                  </div>
                </div>
                <div className="space-y-1 text-xs ml-6">
                  {pending && (
                    <p className="text-amber-600 font-semibold">채점 대기 — 선생님이 확인 후 채점합니다</p>
                  )}
                  {q.type === 'essay' && !pending && (
                    <p>
                      <span className="text-gray-400">득점: </span>
                      <span className={`font-semibold ${full ? 'text-emerald-700' : zero ? 'text-red-700' : 'text-amber-700'}`}>
                        {earned} / {pts}점
                      </span>
                    </p>
                  )}
                  <p>
                    <span className="text-gray-400">내 답: </span>
                    <span className={`whitespace-pre-wrap font-semibold ${
                      pending ? 'text-gray-700' : full ? 'text-emerald-700' : zero ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {a?.raw || '(미입력)'}
                    </span>
                  </p>
                  {q.acceptedAnswers.length > 0 && (
                    <p>
                      <span className="text-gray-400">정답: </span>
                      <span className="text-gray-800 font-semibold">{q.acceptedAnswers[0]}</span>
                      {q.acceptedAnswers.length > 1 && (
                        <span className="text-gray-400"> (또는 {q.acceptedAnswers.slice(1).join(', ')})</span>
                      )}
                    </p>
                  )}
                  {q.explanation && (
                    <p className="text-gray-500 leading-relaxed pt-1 whitespace-pre-wrap">
                      <span className="text-gray-400">해설: </span>{q.explanation}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <button
          onClick={backToList}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-sm text-gray-500"
        >
          <RotateCcw size={14} /> 확인평가 목록
        </button>
      </div>
    )
  }

  return null
}
